'use client';

import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';

import { cn } from '../lib/utils';
import { ModalRoot, useForceVeil } from '../lib/modal-veil';
import { Button } from './button';
import { XIcon } from '@phosphor-icons/react';

function Dialog({ ...props }: Readonly<DialogPrimitive.Root.Props>) {
  return (
    <ModalRoot.Provider value={props.modal ?? true}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </ModalRoot.Provider>
  );
}

function DialogTrigger({ ...props }: Readonly<DialogPrimitive.Trigger.Props>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: Readonly<DialogPrimitive.Portal.Props>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: Readonly<DialogPrimitive.Close.Props>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/* Só PUBLICA a largura da barra de rolagem; quem a usa é a regra
   `html:has([data-slot='dialog-overlay'])` do `styles.css`, que esconde a barra
   pra o véu alcançar a borda e devolve a mesma largura como `padding` no body,
   pra a página não alargar no instante em que o diálogo abre.
   A medida sai daqui porque CSS não sabe medir a barra, mas o padding NÃO é
   aplicado aqui: amarrado à mesma condição do `overflow`, ele entra e sai junto,
   sem depender de o cleanup rodar antes da animação de saída terminar.
   A sonda funciona com ou sem trava de scroll; em plataforma de barra sobreposta
   (macOS) dá 0, a variável fica em 0 e nada muda.
   A sonda mede a barra da PLATAFORMA, não a do documento: se o documento não rola
   (o web-chat rola por dentro da casca), não há barra a esconder e o recuo só
   estreitaria a página (#4962). Daí o 0 nesse caso. `scrollHeight` segue valendo
   sob o `overflow: hidden` do véu, e a trava do Base UI só escreve no body num
   `setTimeout` depois deste efeito. */
/* Prefixo `use` (e não `usa`) porque isto É um custom hook: sem ele o plugin de
   regras de hooks do React não fiscaliza a chamada, e um dia alguém a põe dentro
   de um `if` sem nada acusar. */
function useLarguraDaBarra() {
  React.useLayoutEffect(() => {
    const raiz = document.documentElement;
    if (raiz.scrollHeight <= raiz.clientHeight) {
      raiz.style.setProperty('--barra-de-rolagem', '0px');
      return;
    }
    const sonda = document.createElement('div');
    sonda.style.cssText =
      'position:absolute;visibility:hidden;overflow:scroll;width:50px;height:50px';
    document.body.appendChild(sonda);
    const barra = sonda.offsetWidth - sonda.clientWidth;
    sonda.remove();
    raiz.style.setProperty('--barra-de-rolagem', `${String(barra)}px`);
  }, []);
}

function DialogOverlay({
  className,
  blur = true,
  forceRender,
  ...props
}: DialogPrimitive.Backdrop.Props & {
  /** `false` deixa a página atrás NÍTIDA. */
  blur?: boolean;
}) {
  useLarguraDaBarra();
  const force = useForceVeil();
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        // VOLL: `genui2` aqui não é cosmético — sem ele o `--sand-900` do
        // `bg-(--sand-900)/20` não existe (o backdrop sai por Portal, fora da
        // raiz) e o véu pinta TRANSPARENTE. Ficou invisível enquanto o
        // `backdrop-blur` fazia o trabalho de separar sozinho.
        'genui2',
        // VOLL: véu em INK com 20% no lugar do `bg-black/10` de fábrica, e
        // `blur-sm` no lugar do `xs`. Dois motivos: preto a 10% sobre a página
        // creme lê cinza e frio (o mesmo problema que o scrim do card de foto
        // teve), e 10% é fraco demais pra separar um modal de uma
        // página clara, o que faz o diálogo parecer flutuar sem chão. Ink a 20%
        // aprofunda dentro da família da escala. A referência do Claude Design
        // usa `rgba(33,32,28,0.28)` + blur 6px, que é a mesma ideia.
        'fixed inset-0 isolate z-(--z-popup) bg-(--sand-900)/20 duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        blur && 'supports-backdrop-filter:backdrop-blur-sm',
        className,
      )}
      // VOLL: the veil is the DS default even for a dialog NESTED in another (a modal over a
      // drawer): Base UI skips a nested backdrop unless forced, which left the drawer below bright.
      // Forced only for a modal root (`lib/modal-veil.ts`).
      forceRender={forceRender ?? force}
      {...props}
    />
  );
}

/**
 * `variant="screen"` é a **tela em diálogo**: cobre a janela inteira por cima do que estiver em
 * cena (configurações, mapa e resultados do carro, as telas de resultado da conversa). Entra só
 * com fade — sem o zoom do diálogo centrado, que numa tela cheia lê como a página pulando — e
 * nasce sem véu (ela já cobre a página; o escurecimento só piscaria na entrada) e sem o ✕ fixo
 * (a tela traz a própria barra). O painel que entra pela direita SEM cobrir a página é o
 * `SidePanel` (`./side-panel`).
 */
/** The centered modal's ruler, from `sm` (below it the modal is the screen minus the gutter): a
 *  confirmation or a short form, a form with a list, a richer document or a flow. Same steps as the
 *  side surfaces' `narrow`/`medium` (`lib/side-surface.ts`), plus the old default 384. */
const DIALOG_SIZE = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-xl',
} as const;

type DialogSize = keyof typeof DIALOG_SIZE;

function DialogContent({
  className,
  children,
  variant = 'default',
  size = 'sm',
  showCloseButton = variant === 'default',
  showOverlay = variant === 'default',
  blurBackdrop = true,
  recuoDoVeu,
  closeLabel = 'Close',
  ...props
}: DialogPrimitive.Popup.Props & {
  variant?: 'default' | 'screen';
  /** Width of the centered modal: `sm` 384 · `md` 448 · `lg` 576. Ignored by `screen`. */
  size?: DialogSize;
  showCloseButton?: boolean;
  /** VOLL: `false` remove o véu por inteiro — pro painel de tela cheia que já cobre a página, o
   *  escurecimento só aparece durante o slide de entrada/saída e lê como um flash sem função. */
  showOverlay?: boolean;
  /** `false` mantém a página atrás nítida. */
  blurBackdrop?: boolean;
  /** VOLL: recua o véu pela esquerda, pra o menu lateral do shell ficar NÍTIDO e clicável enquanto
   *  o diálogo de tela cheia está aberto. Combina com o mesmo recuo no popup. */
  recuoDoVeu?: string;
  /** Nome acessível do botão de fechar; consumidores de superfície de cliente passam a copy
   *  traduzida. */
  closeLabel?: string;
}) {
  return (
    <DialogPortal>
      {showOverlay ? (
        <DialogOverlay blur={blurBackdrop} style={recuoDoVeu ? { left: recuoDoVeu } : undefined} />
      ) : null}
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        data-variant={variant}
        className={cn(
          // VOLL: `genui2` obrigatório em tudo que sai por Portal — ver a nota
          // no sheet.tsx e a regra no CLAUDE.md da pasta.
          'genui2',
          'fixed z-(--z-popup) duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
          // A tela em diálogo é uma PÁGINA: herda o tamanho e a cor do texto de
          // quem a monta, como a página que ela substitui.
          variant === 'screen'
            ? 'inset-0 flex flex-col overflow-hidden bg-background'
            : 'top-1/2 left-1/2 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 data-open:zoom-in-95 data-closed:zoom-out-95',
          variant === 'default' && DIALOG_SIZE[size],
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        // Botão sempre HUG e à direita, em linha — chamada do designer (2026-07-28), contra o
        // `flex-col-reverse sm:flex-row` do preset: o `sm:` é do VIEWPORT, e o diálogo é um
        // container estreito, então no WebView do app os botões esticavam a largura toda.
        '-mx-4 -mb-4 flex flex-row justify-end gap-2 rounded-b-xl border-t bg-muted/50 p-4',
        className,
      )}
      {...props}
    >
      {/* Fechar ANTES da ação primária: a primária é a última, à direita (mesma ordem do print). */}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Cancelar
        </DialogPrimitive.Close>
      )}
      {children}
    </div>
  );
}

function DialogTitle({ className, ...props }: Readonly<DialogPrimitive.Title.Props>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      // VOLL: `font-sans` no lugar do `font-heading` de fábrica — `--font-heading` mapeia pra
      // Fraunces, tipografia decorativa que exige aprovação do designer; título de modal usa Geist.
      className={cn('font-sans text-base leading-none font-medium', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: Readonly<DialogPrimitive.Description.Props>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
